import React, { useState, useRef, useEffect } from 'react';
import { storage } from '../../../../firebase/firebaseConfig';
import { ref, uploadBytes } from 'firebase/storage';
import AvatarEditor from 'react-avatar-editor';
import defaultPic from '../../../Images/BitBlue-Person-Default-Profile-Picture.png';
import { settingsFunctions } from '../../../../functions/settings';
import { useAuth } from '../../../../utils/Auth';
import styles from './ProfilePicEditor.module.css';

function ProfilePicEditor(props) {
    const { setShowForm, profile_pic, uid, setStep } = props;
    const { updateVcardObject } = settingsFunctions;
    const { getUpdatedUserVcard } = useAuth();
    const editorRef = useRef();
    
    const [ edits, setEdits ] = useState({
        position: {x: 0.5, y: 0.5},
        scale: 1,
        file: null,
        fileName: '',
    })
    const [ showSpinner, setShowSpinner ] = useState(false);

    useEffect(() => {
        if (profile_pic) {
            setEdits(prev => ({
                ...prev,
                file: `${profile_pic}?${Date.now()}`
            }));
        } else {
            setEdits(prev => ({
                ...prev,
                file: defaultPic
            }));
        };
    },[profile_pic]);

    const handleReset = () => {
        const input = document.getElementById('profile-pic-img');
        input.value = '';
        setEdits(prev => ({
            ...prev,
            file: profile_pic ? `${profile_pic}?${Date.now()}` : defaultPic,
            fileName: ''
        }));
    };

    const handlePhotoToEdit = (e) => {
        setEdits(prev => ({
            ...prev,
            file: e.target.files[0],
            fileName: e.target.files[0].name
        }));
    };

    const handleScale = (e) => {
        const scale = parseFloat(e.target.value);
        setEdits(prev => ({
            ...prev,
            scale: scale
        }));
    };

    const handlePositionChange = (position) => {
        setEdits(prev => ({
            ...prev,
            position: position
        }))
    };

    const handlePorfilePicSave = () => {
        if (editorRef.current) {
            editorRef.current.getImageScaledToCanvas().toBlob(async blob => {
                const newFile = new File([blob], 'profile_image', {lastModified: Date.now(), type: blob.type});
                const fileName = `${newFile.name}.${newFile.type.slice(newFile.type.indexOf('/') + 1)}`;
                const storageRef = ref(storage, `${uid}/${fileName}`);

                try {
                    setShowSpinner(true);
                    await uploadBytes(storageRef, newFile);
                    const vcardEdit = {};
                    vcardEdit.profile_pic = `https://storage.googleapis.com/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${uid}/${fileName}`;
                    await updateVcardObject(vcardEdit, uid);

                    if (window.location.pathname.includes('wizard')) {
                        setStep(prev => (prev + 1));
                    } else {
                        await getUpdatedUserVcard().then(() => {
                            setShowSpinner(false);
                            setShowForm('');
                        });
                    };

                } catch (err) {
                    console.log('Error setting profile picture', err);
                };
            });
        };
    };

    const handleRemoveProfilePic = async () => {
        const vcardEdit = {};
        vcardEdit.profile_pic = '';
        setShowSpinner(true);
        await updateVcardObject(vcardEdit, uid).then(async () => {
            if (window.location.pathname.includes('wizard')) {
                setStep(prev => (prev + 1));
            } else {
                await getUpdatedUserVcard().then(() => {
                    setShowSpinner(false);
                    setShowForm('');
                });
            };
        });
    };

    useEffect(() => {
        const inputRange = document.getElementById("profile-pic-zoom");

        const handleInputColor = e => {
            const { value, min, max } = e.target;
            const ratio = (value - min) / (max - min) * 100;
            inputRange.style.background = `linear-gradient(90deg, #364957 ${ratio}%, #cccccc ${ratio}%)`;
        };

        handleInputColor({target: inputRange});

        inputRange.addEventListener("input", handleInputColor);
        return () => inputRange.removeEventListener("input",  handleInputColor);
    },[]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.backBtn} onClick={e => {e.preventDefault(); window.location.pathname.includes('wizard') ? setStep(0) : setShowForm("")}}>
                    <i className='fas fa-arrow-left'></i>
                    Back
                </div>
                Edit Profile Picture
                <div className={styles.resetBtn} onClick={handleReset}>
                    Reset
                </div>
            </div>
            <AvatarEditor
                className={styles.editor}
                crossOrigin='anonymous'
                ref={editorRef}
                borderRadius={100}
                border={25}
                image={edits.file}
                scale={edits.scale}
                width={200}
                height={200}
                position={edits.position}
                onPositionChange={handlePositionChange}
                color={[0,0,0,0.4]}
            />
            <div className={styles.inputContainer}>
                <label htmlFor='profile-pic-img-input' className={styles.label}>Upload Image</label>
                <input
                    type='file'
                    id='profile-pic-img-input'
                    onChange={handlePhotoToEdit}
                    accept='image/*'
                />
                <div>{edits?.fileName ? edits.fileName : 'No File Selected'}</div>
            </div>
            <div className={styles.rangeContainer}>
                <label htmlFor='profile-pic-zoom'>Zoom</label>
                <input 
                    type="range"
                    id='profile-pic-zoom' 
                    className={styles.profilePicZoom}
                    min="0.5" 
                    max="3" 
                    step='0.25'
                    value={edits.scale}
                    onChange={handleScale}
                />
            </div>
            <div className={styles.inputGroup}>
                <button className={styles.applyBtn} onClick={handlePorfilePicSave} disabled={!edits?.file || showSpinner}>{showSpinner ? <i className="fa-thin fa-spinner fa-spin-pulse"></i> : "Apply Changes"}</button>
                <button className={styles.removeBtn} onClick={handleRemoveProfilePic} disabled={!edits?.file}>Remove</button>
            </div>
        </div>
    );
};

export default ProfilePicEditor;